﻿import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { searchStocks } from "../utils/stockApi.js";
import { goToStock } from "../utils/route.js";

// Type anywhere on the site (outside a text box) and this search popup opens.
export default function QuickSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const seq = useRef(0);

  // open on the first typed character
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1 || !/[A-Za-z0-9.\-]/.test(e.key)) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      if (document.querySelector(".modal.show")) return;
      e.preventDefault();
      setQ(e.key);
      setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [open]);

  // search as you type
  useEffect(() => {
    if (!open) return undefined;
    const id = ++seq.current;
    if (!q.trim()) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      searchStocks(q, 8).then((r) => {
        if (id === seq.current) { setResults(r || []); setActive(0); }
      }).catch(() => {});
    }, 100);
    return () => clearTimeout(timer);
  }, [q, open]);

  const close = () => { setOpen(false); setQ(""); setResults([]); };
  const choose = (ticker) => { close(); goToStock(ticker); setTimeout(() => window.dispatchEvent(new HashChangeEvent("hashchange")), 0); };

  const onKeyDown = async (e) => {
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (!q.trim()) return;
      if (results[active]) return choose(results[active].ticker);
      setBusy(true); // results not back yet: search now and open the best match
      try {
        const r = await searchStocks(q, 8);
        if (r && r.length) choose(r[0].ticker);
        else setResults([]);
      } finally { setBusy(false); }
    }
  };

  if (!open) return null;
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,.45)", display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: "12vh" }}
    >
      <div style={{ width: "min(640px, 92vw)", background: "var(--bs-body-bg, #fff)", color: "var(--bs-body-color, #212529)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.35)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(128,128,128,.25)" }}>
          <Search size={18} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search company or ticker"
            aria-label="Search companies"
            autoComplete="off"
            spellCheck={false}
            style={{ flex: 1, border: 0, outline: 0, background: "transparent", color: "inherit", fontSize: 18 }}
          />
          <small style={{ opacity: 0.6 }}>Enter to open · Esc to close</small>
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: "50vh", overflowY: "auto" }}>
          {q.trim() && !results.length && (
            <li style={{ padding: "14px 16px", opacity: 0.7 }}>{busy ? "Searching…" : "No matching companies"}</li>
          )}
          {results.map((c, i) => (
            <li
              key={c.ticker}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(c.ticker); }}
              style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "10px 16px", cursor: "pointer", background: i === active ? "rgba(124,58,237,.14)" : "transparent" }}
            >
              <strong style={{ minWidth: 64 }}>{c.ticker}</strong>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
              <small style={{ opacity: 0.6 }}>{c.industry}</small>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

